import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { MoreHorizontalIcon, PencilIcon, EyeIcon, ArchiveIcon } from "lucide-react"
import { Link } from "react-router-dom"

export type CohortStatus = "enrolling" | "in-progress" | "in_progress" | "completed" | "archived" | "draft" | string

export interface Cohort {
  id: string
  slug?: string
  name: string
  status: CohortStatus
  startDate: string
  endDate: string
  enrolled: number
  capacity: number
  revenue: number
}

interface CohortTableRowProps {
  cohort: Cohort
  onEdit?: (cohort: Cohort) => void
  onView?: (cohort: Cohort) => void
  onArchive?: (cohort: Cohort) => void
}

const statusConfig: Record<string, { label: string; className: string }> = {
  enrolling: {
    label: "Enrolling",
    className: "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary",
  },
  "in-progress": {
    label: "In Progress",
    className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  in_progress: {
    label: "In Progress",
    className: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
  },
  completed: {
    label: "Completed",
    className: "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200",
  },
  archived: {
    label: "Archived",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
  },
  draft: {
    label: "Draft",
    className: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  },
}

export function CohortTableRow({
  cohort,
  onEdit,
  onView,
  onArchive,
}: CohortTableRowProps) {
  const statusInfo = statusConfig[cohort.status] || { label: cohort.status, className: "bg-gray-100 text-gray-600" }
  const enrollmentPercentage = (cohort.enrolled / cohort.capacity) * 100

  return (
    <tr className="border-b border-border hover:bg-muted/50 transition-colors">
      <td className="px-6 py-4">
        <div className="font-medium text-foreground">{cohort.name}</div>
      </td>
      <td className="px-6 py-4">
        <Badge className={statusInfo.className}>{statusInfo.label}</Badge>
      </td>
      <td className="px-6 py-4 text-muted-foreground">{cohort.startDate}</td>
      <td className="px-6 py-4 text-muted-foreground">{cohort.endDate}</td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className="text-foreground font-medium">
            {cohort.enrolled} / {cohort.capacity}
          </span>
          <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${Math.min(enrollmentPercentage, 100)}%` }}
            />
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="text-foreground font-medium">
          ${cohort.revenue.toLocaleString()}
        </span>
      </td>
      <td className="px-6 py-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => onView?.(cohort)}
              className="cursor-pointer"
            >
              <EyeIcon className="h-4 w-4 mr-2" />
              View Details
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onEdit?.(cohort)}
              className="cursor-pointer"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit
            </DropdownMenuItem>
            {cohort.status !== "archived" && (
              <DropdownMenuItem
                onClick={() => onArchive?.(cohort)}
                className="cursor-pointer text-amber-600 dark:text-amber-400"
              >
                <ArchiveIcon className="h-4 w-4 mr-2" />
                Archive
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}
